/// <reference types="google.maps" />
import { Map as GMap, useMap } from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Renderer,
} from "@googlemaps/markerclusterer";
import { useEffect, useMemo, useRef } from "react";
import type { ResolvedPlace, Store } from "../../shared/types";
import type { LatLng } from "../lib/geoUtils";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from "../lib/env";
import { MissingKeyNotice } from "./MissingKeyNotice";
import { getTier, TIER_CHIP_CLASS } from "../lib/tier";

// GMap の style prop は毎 render 新 object にすると下流で referential equality が
// 壊れるため、モジュール定数として固定する。
const GMAP_STYLE = { width: "100%", height: "100%" } as const;

const DEFAULT_CENTER = { lat: 35.6812, lng: 139.7671 }; // Tokyo Station
const DEFAULT_ZOOM = 11;

/**
 * リスト/サイドメニュー/Sheet から店舗が選択されたときに最低限保証するズーム。
 * 全国表示 (z11) のままだと選択ピンの周辺が読み取れないので、ここまでは寄せる。
 * ユーザーが既により拡大している場合は縮めず、現在ズームを尊重する。
 *
 * z14 はブロック単位で主要交差点や駅が視界に入るスケール。
 */
const MIN_SELECTION_ZOOM = 14;

/**
 * クラスタリングを有効にする最大ズーム。
 * この zoom 以下では `MarkerClusterer` (SuperCluster) がマーカーを集約し、
 * これ以上にズームインすると個別マーカーに展開される。
 *
 * 16 を採用: 街区レベル (~300m scale) までは集約し、それ以上寄った時のみ
 * 個別マーカーを表示する。SP GPU 負荷の上限を安定させる狙い。
 */
const CLUSTER_MAX_ZOOM = 16;

/**
 * 現在地に寄せる時のズーム。半径 1-2km 程度を視野に入れる値。
 * 「徒歩圏外も含めて近くの選択肢を俯瞰したい」ケースに合わせ、街区より広い
 * エリア (駅複数個分) が入る程度の引き気味の初期ビューにする。ユーザーが
 * さらに寄りたい場合は手動でピンチ/スクロール拡大する前提。
 */
const NEARBY_FIT_ZOOM = 13;

type MarkerItem = { store: Store; resolved: ResolvedPlace };

interface Props {
  stores: Store[];
  resolvedById: ReadonlyMap<string, ResolvedPlace>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /**
   * ワード検索が確定済み (committedQuery が非空) かどうか。true のとき、
   * fitBounds の代わりに「結果の重心 + 固定ズーム」で寄せる。
   */
  searchActive: boolean;
  /**
   * true: `@googlemaps/markerclusterer` で近接ピンを集約表示する (デフォルト、SP で軽い)。
   * false: 全マーカーを個別に描画する (見た目優先、マーカーが多いと SP で重くなる)。
   */
  clusterMode: boolean;
  /**
   * 現在地 (nearby mode)。非 null のとき:
   *   - 青ドットマーカーを地図上に表示
   *   - FitBounds を nearby-mode 分岐に切替え、現在地を中心にパンする
   * null なら通常 (stores fit)。
   */
  userLocation: LatLng | null;
}

export function MapView({
  stores,
  resolvedById,
  selectedId,
  onSelect,
  searchActive,
  clusterMode,
  userLocation,
}: Props) {
  // resolve 済みマーカーの素リストを構築する。
  // クラスタリングは `@googlemaps/markerclusterer` (SuperCluster) に委ねるので、
  // 以前のような座標キー集約や EXPAND_ZOOM ベースの個別展開ロジックは撤去した。
  const items = useMemo<MarkerItem[]>(() => {
    const raw: MarkerItem[] = [];
    for (const store of stores) {
      const resolved = resolvedById.get(store.id);
      if (resolved) raw.push({ store, resolved });
    }
    return raw;
  }, [stores, resolvedById]);

  if (!GOOGLE_MAPS_API_KEY) {
    return <MissingKeyNotice />;
  }

  return (
    <GMap
      mapId={GOOGLE_MAPS_MAP_ID}
      defaultCenter={DEFAULT_CENTER}
      defaultZoom={DEFAULT_ZOOM}
      gestureHandling="greedy"
      disableDefaultUI={false}
      mapTypeControl={false}
      streetViewControl={false}
      fullscreenControl={false}
      zoomControl
      // Google ベースマップ上の POI クリックを無効化。
      // カスタムマーカーが Google POI と同位置にあると、クリック時に
      // ネイティブの赤ピン + Place カードが重複して表示されてしまうのを防ぐ。
      clickableIcons={false}
      style={GMAP_STYLE}
      reuseMaps
    >
      <FitBounds
        stores={stores}
        markers={items}
        selectedId={selectedId}
        searchActive={searchActive}
        userLocation={userLocation}
      />
      <ClusteredMarkerLayer
        items={items}
        selectedId={selectedId}
        onSelect={onSelect}
        clusterMode={clusterMode}
      />
      <UserLocationMarker location={userLocation} />
    </GMap>
  );
}

// ─── ClusteredMarkerLayer ────────────────────────────────────────────────────
//
// 以前は React の `<StoreMarker>` を 200 個ほど JSX で描画していたが、
// SP GPU で pan 時にコマ落ちする主要因となっていた (200 ネスト DOM + 200
// レイヤ昇格)。
//
// この実装は React tree の外に出て以下を実現する:
//   1. 店舗マーカーを `document.createElement("div")` で素 DOM として生成
//   2. それを `google.maps.marker.AdvancedMarkerElement` に content として渡す
//   3. `@googlemaps/markerclusterer` に全部放り込み、zoom に応じて自動集約
//   4. items 差分で add/remove を最小限に抑える
//   5. 選択状態は DOM の classList 操作で反映 (React 再レンダ不要)
//
// これで: 200 個の React 再レンダがゼロ、合成レイヤは clusterer の出力 (低 zoom
// では 15-30 個のクラスタバブル) のみ。
//
// マーカーの見た目は `buildMarkerContent` で tier / 閉店 / 移転 / 選択状態を
// Tailwind クラス文字列で組み立てる。Tailwind は src 全体をスキャンしているので
// TIER_CHIP_CLASS 等で参照されているクラスは build 成果物に含まれる。
// ─────────────────────────────────────────────────────────────────────────────

function ClusteredMarkerLayer({
  items,
  selectedId,
  onSelect,
  clusterMode,
}: {
  items: MarkerItem[];
  selectedId: string | null;
  /** 親で安定参照であること (useCallback / setState setter)。 */
  onSelect: (id: string) => void;
  /** true: SuperCluster で集約。false: 全マーカーを map に直接 attach。 */
  clusterMode: boolean;
}) {
  const map = useMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  // storeId → marker instance。差分更新用。
  const markersByIdRef = useRef(
    new Map<string, google.maps.marker.AdvancedMarkerElement>(),
  );
  // onSelect の最新値を ref で保持し、addListener の closure から参照する。
  // effect 再実行による全マーカーの listener 張り替えを避けるため。
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // clusterer の初期化 / 破棄 + モード切替時の既存マーカー移送。
  //
  // clusterMode を切り替えた時のポイント:
  //   既存の AdvancedMarkerElement インスタンスは markersByIdRef に生きたまま保持し、
  //   「古いモードから外す → 新しいモードに attach し直す」だけを行う。
  //   これで 200 件の recreate を避けつつ、画面からピンが消える事故も防げる。
  //
  //   cluster → individual : clusterer から外す → 各 marker.map = map で直接 attach
  //   individual → cluster : 全 marker.map = null → 新 clusterer に addMarkers
  //
  // 下の items-diff effect も `clusterMode` を deps に入れているが、こちらの
  // 移送ロジックで既存マーカーは既に正しいモードに載っているため、
  // items が変わっていなければ diff effect は no-op になる (= 冗長な再 attach を回避)。
  useEffect(() => {
    if (!map) return;

    const current = markersByIdRef.current;

    if (clusterMode) {
      // Cluster モードに入る: 新 clusterer を作り、既存マーカーを全部流し込む。
      const clusterer = new MarkerClusterer({
        map,
        algorithm: new SuperClusterAlgorithm({
          radius: 60,
          maxZoom: CLUSTER_MAX_ZOOM,
        }),
        renderer: clusterRenderer,
      });
      clustererRef.current = clusterer;

      if (current.size > 0) {
        // individual モードから移ってきた場合は marker.map が直接 map を指している。
        // clusterer が管理できるよう一度 detach してから addMarkers する。
        for (const m of current.values()) m.map = null;
        clusterer.addMarkers(Array.from(current.values()));
      }
    } else {
      // Individual モードに入る: clusterer を作らず、既存マーカーを直接 map に attach。
      // 前モードが cluster だった場合、clusterer は cleanup で先に解除されるので
      // marker.map は null になっている (clusterer.removeMarkers / clearMarkers の副作用)。
      for (const m of current.values()) m.map = map;
    }

    return () => {
      const c = clustererRef.current;
      if (c) {
        // Cluster → 別モード への移行 cleanup。
        // clusterer から全マーカーを外す (marker.map が null になる副作用付き)
        // が、markersByIdRef のインスタンス自体は破棄せず、次モードで再利用する。
        c.clearMarkers();
        c.setMap(null);
        clustererRef.current = null;
      } else {
        // Individual → 別モード への移行 cleanup。
        // 各 marker を map から切り離すだけ。インスタンスは保持。
        for (const m of current.values()) m.map = null;
      }
    };
  }, [map, clusterMode]);

  // items の差分を計算して add/remove する。clusterMode で分岐:
  //   - clusterMode=true  : clusterer.addMarkers / removeMarkers を使う
  //   - clusterMode=false : 各マーカーの `.map` プロパティを直接操作
  //
  // clusterMode を deps に入れているので、モード切替時は:
  //   1. 前回の cleanup (if any) で古いマーカーを現モードから外す
  //   2. 再実行で markersByIdRef を空にし、全マーカーを新モードに向けて attach
  useEffect(() => {
    if (!map) return;

    const clusterer = clustererRef.current;
    const current = markersByIdRef.current;
    const nextIds = new Set<string>();
    for (const item of items) nextIds.add(item.store.id);

    // 1) remove stale
    const toRemove: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const [id, marker] of current) {
      if (!nextIds.has(id)) {
        toRemove.push(marker);
        current.delete(id);
      }
    }
    if (toRemove.length > 0) {
      if (clusterer) {
        clusterer.removeMarkers(toRemove, true);
      } else {
        for (const m of toRemove) m.map = null;
      }
    }

    // 2) add new
    const toAdd: google.maps.marker.AdvancedMarkerElement[] = [];
    for (const item of items) {
      if (current.has(item.store.id)) continue;
      const content = buildMarkerContent(
        item.store,
        selectedId === item.store.id,
      );
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: item.resolved.lat, lng: item.resolved.lng },
        content,
        title: `${item.store.name} (${item.store.selectionCount}回選出)`,
      });
      // AdvancedMarker の click イベントは "gmp-click"。
      // onSelect は ref 経由で最新を参照する (listener 張り替え不要)。
      marker.addListener("gmp-click", () => {
        onSelectRef.current(item.store.id);
      });
      current.set(item.store.id, marker);
      toAdd.push(marker);
    }
    if (toAdd.length > 0) {
      if (clusterer) {
        clusterer.addMarkers(toAdd, true);
      } else {
        // 非 cluster モード: 全マーカーを map に直接 attach する。
        for (const m of toAdd) m.map = map;
      }
    }

    // cluster モードでは noDraw=true 蓄積後にまとめて 1 回 render する。
    if (clusterer && (toRemove.length > 0 || toAdd.length > 0)) {
      clusterer.render();
    }
    // selectedId は意図的に deps から除外 (選択変化は別 effect で class toggle のみ)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, map, clusterMode]);

  // 選択状態の反映: 対応するマーカーの content に Tailwind クラスを toggle。
  // React 再レンダなし (imperative DOM 操作)。
  // scale-110 / ring-2 / ring-white はそのまま SP GPU の合成負荷に乗るが
  // 発動するのは常に 1 個だけなので無視できる。
  useEffect(() => {
    for (const [id, marker] of markersByIdRef.current) {
      const el = marker.content as HTMLElement | null;
      if (!el) continue;
      const selected = id === selectedId;
      el.classList.toggle("scale-110", selected);
      el.classList.toggle("ring-2", selected);
      el.classList.toggle("ring-white", selected);
    }
  }, [selectedId]);

  return null;
}

/**
 * クラスタバブルの見た目。AdvancedMarkerElement で構築して
 * mapId (vector map) と整合させる。デフォルト renderer は legacy Marker を
 * 使うため mapId と噛み合わないことがあり、カスタム renderer で置き換える。
 *
 * 配色: 既存 tier パレット (sky/emerald/amber/rose) と被らないよう indigo → violet
 * グラデーションを採用。tier 4 (amber 黄色) との視覚的混同を避ける。
 * 濃い背景 + 白テキストで「集約表示」であることを明確化。
 */
const clusterRenderer: Renderer = {
  render({ count, position }) {
    const content = document.createElement("div");
    content.className =
      "-translate-y-1/2 whitespace-nowrap rounded-full border-2 border-white/60 bg-gradient-to-b from-indigo-500 to-violet-700 px-3 py-1 text-[12px] font-bold text-white shadow-md";
    content.textContent = `${count}店舗`;
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content,
    });
  },
};

/**
 * 個別マーカーの DOM を生成する。React を介さず document.createElement で
 * 1 要素に絞る (旧 React 実装は 5 ネスト要素 × 200 で SP GPU が詰まっていた)。
 */
function buildMarkerContent(store: Store, selected: boolean): HTMLElement {
  const closed = store.status === "closed" || store.movedToClosed === true;
  const moved = store.status === "moved" && !store.movedToClosed;
  const renewed = store.status === "renewed";
  const tier = !closed && !renewed && !moved ? getTier(store.selectionCount) : null;

  // ステータス / 選出回数サフィックスを 1 テキストに連結。要素を増やさない。
  const suffix = closed
    ? " · 閉店"
    : renewed
      ? " · リニューアル"
      : moved
        ? " · 移転"
        : tier
          ? ` · ${store.selectionCount}`
          : "";

  const div = document.createElement("div");
  const classes = [
    "-translate-y-1/2",
    "max-w-[10rem]",
    "truncate",
    "whitespace-nowrap",
    "rounded-full",
    "border",
    "px-2.5",
    "py-1",
    "text-[11px]",
    "font-semibold",
    "shadow-sm",
  ];
  if (tier) classes.push(TIER_CHIP_CLASS[tier]);
  if (closed || renewed) {
    classes.push("border-slate-600/60", "bg-slate-800/90", "text-slate-400");
  } else if (moved) {
    classes.push("border-teal-400/50", "bg-slate-800/90", "text-teal-200");
  }
  if (selected) classes.push("scale-110", "ring-2", "ring-white");
  // selected 時に class を toggle する際のキー class。
  classes.push("store-marker");
  div.className = classes.join(" ");
  div.textContent = `${store.name}${suffix}`;
  return div;
}

/**
 * 初回 + フィルタ/検索が変わった時に bounds を合わせる。
 * 選択時はパンだけ (ズームは維持) して店舗を画面内に寄せる。
 *
 * storesFitKey (length + 先頭 id + 末尾 id) で内容比較するので、
 * 参照だけ変わっても内容が同じなら再 fit しない。
 */
function FitBounds({
  stores,
  markers,
  selectedId,
  searchActive,
  userLocation,
}: {
  stores: Store[];
  markers: MarkerItem[];
  selectedId: string | null;
  searchActive: boolean;
  userLocation: LatLng | null;
}) {
  const map = useMap();
  const lastFitKeyRef = useRef<string | null>(null);
  const lastNearbyKeyRef = useRef<string | null>(null);
  const hasMarkers = markers.length > 0;
  const storesFitKey =
    stores.length === 0
      ? ""
      : `${stores.length}|${stores[0]!.id}|${stores[stores.length - 1]!.id}`;
  // 現在地座標を fingerprint 化。小数 4 桁 ≒ 11m 精度、この粒度で pan する。
  // 精度を上げすぎると GPS ノイズで頻繁に panTo が走ってしまう。
  const nearbyKey = userLocation
    ? `${userLocation.lat.toFixed(4)}|${userLocation.lng.toFixed(4)}`
    : "";

  // Nearby mode: userLocation が変わったら map を現在地に寄せる。
  //
  // 注意: lastFitKeyRef を触らない。
  //   以前はここで `lastFitKeyRef.current = null` にリセットしていたが、
  //   その結果 nearby 解除時に stores-fit が強制発火し、ユーザーが nearby 中に
  //   ピンしていた現在地からいきなり Tokyo 全体 fit に吹き飛ばされていた。
  //   content-fingerprint ベースの stores fit ロジックは自律的に判断するので、
  //   nearby 解除後も storesFitKey が変わっていなければ再 fit しない
  //   (= ユーザーの直前のカメラ位置を尊重する)。
  useEffect(() => {
    if (!map) return;
    if (!userLocation) {
      lastNearbyKeyRef.current = null;
      return;
    }
    if (lastNearbyKeyRef.current === nearbyKey) return;
    lastNearbyKeyRef.current = nearbyKey;

    map.panTo(userLocation);
    map.setZoom(NEARBY_FIT_ZOOM);
  }, [map, nearbyKey, userLocation]);

  // 通常の stores fit。nearby mode 中はスキップ (上の effect が優先)。
  useEffect(() => {
    if (!map) return;
    if (userLocation) return; // nearby mode では stores fit を行わない
    if (lastFitKeyRef.current === storesFitKey) return;
    if (!hasMarkers) return;
    lastFitKeyRef.current = storesFitKey;

    if (searchActive) {
      let sumLat = 0;
      let sumLng = 0;
      for (const { resolved } of markers) {
        sumLat += resolved.lat;
        sumLng += resolved.lng;
      }
      const center = {
        lat: sumLat / markers.length,
        lng: sumLng / markers.length,
      };
      map.panTo(center);
      map.setZoom(MIN_SELECTION_ZOOM);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const { resolved } of markers) {
      bounds.extend({ lat: resolved.lat, lng: resolved.lng });
    }
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 80);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, storesFitKey, hasMarkers, searchActive, userLocation]);

  // pan to selection — 既存ズームより小さければ MIN_SELECTION_ZOOM までアップ。
  const markersRef = useRef(markers);
  markersRef.current = markers;
  useEffect(() => {
    if (!map || !selectedId) return;
    const target = markersRef.current.find((m) => m.store.id === selectedId);
    if (!target) return;
    map.panTo({ lat: target.resolved.lat, lng: target.resolved.lng });
    const currentZoom = map.getZoom() ?? DEFAULT_ZOOM;
    if (currentZoom < MIN_SELECTION_ZOOM) {
      map.setZoom(MIN_SELECTION_ZOOM);
    }
  }, [map, selectedId]);

  return null;
}

/**
 * 現在地を示す青ドットマーカー。
 *
 * - MarkerClusterer には入れず map に直接 attach (常に前面、集約されない)
 * - AdvancedMarkerElement を 1 つ だけ作り、location 変更時は position を上書き
 * - location が null になったら map から detach してマーカーを隠す
 *
 * DOM は "パルスする青ドット" を 2 要素で表現: 外側の半透明円 + 内側の solid 円。
 * Tailwind の animate-ping クラスで outer ring がパルスする視覚効果。
 */
function UserLocationMarker({ location }: { location: LatLng | null }) {
  const map = useMap();
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null,
  );

  useEffect(() => {
    if (!map) return;
    if (!location) {
      // location null → マーカーを map から外す (インスタンスは保持して再利用)
      if (markerRef.current) markerRef.current.map = null;
      return;
    }

    if (!markerRef.current) {
      // 初回のみ DOM + AdvancedMarkerElement を生成
      const content = document.createElement("div");
      content.className = "relative flex h-4 w-4 items-center justify-center";
      // パルスリング (animate-ping)
      const ring = document.createElement("span");
      ring.className =
        "absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400/60";
      // 本体ドット
      const dot = document.createElement("span");
      dot.className =
        "relative inline-flex h-3 w-3 rounded-full bg-sky-500 ring-2 ring-white shadow-md";
      content.appendChild(ring);
      content.appendChild(dot);

      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: location,
        content,
        // zIndex を高めに設定して店舗ピンより前面に出す (仕様上は number を受け付ける)
        zIndex: 9999,
      });
    } else {
      markerRef.current.position = location;
    }
    markerRef.current.map = map;

    return () => {
      // Effect の cleanup では map から外すだけ。インスタンスは次回再利用。
      if (markerRef.current) markerRef.current.map = null;
    };
  }, [map, location]);

  return null;
}
